import {
  ChevronDown,
  Podcast,
  Play,
  Pause,
  Star,
  RotateCcw,
  RotateCw,
  VolumeX,
  Volume2,
  SkipBack,
  SkipForward,
  Shuffle,
  GripHorizontal,
  Layers,
  Globe,
  X,
  Share2,
  Check,
} from "lucide-react";
import React, { useRef, useState, useEffect } from "react";

import { useLanguage } from "../contexts/LanguageContext";
import { Station } from "../types";
import { getStationLogoUrl } from "../utils/imageUtils";

import Equalizer from "./Equalizer";
import HeaderOscilloscope from "./HeaderOscilloscope";

interface Props {
  currentStation: Station | null;
  isPlaying: boolean;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
  showEq: boolean;
  setShowEq: (show: boolean) => void;
  eqGains: number[];
  onEqGainChange: (index: number, value: number) => void;
  favorites: Station[];
  onToggleFavorite: (station: Station) => void;
  onTogglePlay: () => void;
  volume: number;
  setVolume: (vol: number) => void;
  onArtistInfo: () => void;
  onZenMode: () => void;
  isSidebarOpen: boolean;
  onToggleSidebar?: () => void;
  currentTime?: number;
  duration?: number;
  onSeek?: (time: number) => void;
  onSkip?: (seconds: number) => void;
  onNext?: () => void;
  onPrev?: () => void;
  onShuffle?: () => void;
  isShuffling?: boolean;
  playbackSpeed?: number;
  onCyclePlaybackSpeed?: () => void;
  onOpenDiscover: () => void;
  onNavigateToStation?: () => void;
  contextInfo?: {
    type: "grid" | "carousel" | "search" | "favorites" | "related" | "history";
    currentIndex: number;
    totalItems: number;
  } | null;
}

const formatTime = (seconds: number) => {
  if (!seconds || isNaN(seconds)) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
};

const PlayerBar: React.FC<Props> = ({
  currentStation,
  isPlaying,
  isExpanded,
  setIsExpanded,
  showEq,
  setShowEq,
  eqGains,
  onEqGainChange,
  favorites,
  onToggleFavorite,
  onTogglePlay,
  volume,
  setVolume,
  onArtistInfo: _onArtistInfo,
  onZenMode: _onZenMode,
  isSidebarOpen,
  onToggleSidebar: _onToggleSidebar,
  currentTime = 0,
  duration = 0,
  onSeek,
  onSkip,
  onNext,
  onPrev,
  onShuffle,
  isShuffling = false,
  playbackSpeed = 1,
  onCyclePlaybackSpeed,
  onOpenDiscover: _onOpenDiscover,
  onNavigateToStation,
  contextInfo,
}) => {
  const { t, tCountry } = useLanguage();

  const prevVolumeRef = useRef(0.5);
  const [imageError, setImageError] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showStationInfo, setShowStationInfo] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const touchStartY = useRef(0);
  const touchStartX = useRef(0); // For horizontal swipe detection
  const [dragOffset, setDragOffset] = useState(0);

  // Haptic feedback helper
  const triggerHaptic = (duration: number = 10) => {
    if (typeof navigator !== "undefined" && "vibrate" in navigator) {
      navigator.vibrate(duration);
    }
  };

  const isPodcast = currentStation?.provider === "podcast";
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Reset image error when station changes
  useEffect(() => {
    setImageError(false);
  }, [currentStation?.id]);

  // Hint Logic
  useEffect(() => {
    if (isExpanded && currentStation) {
      const hasSeen = localStorage.getItem("se_has_seen_dock_hint");
      if (!hasSeen) {
        setShowHint(true);
        // Auto-dismiss after 5s and mark seen
        const timer = setTimeout(() => {
          setShowHint(false);
          localStorage.setItem("se_has_seen_dock_hint", "true");
        }, 5000);
        return () => clearTimeout(timer);
      }
    } else {
      setShowHint(false);
    }
  }, [isExpanded, currentStation]);

  // Share Handler
  const handleShare = async () => {
    if (!currentStation) return;
    const isPod = currentStation.provider === "podcast";
    const slug = currentStation.id;
    const shareUrl = `${window.location.origin}/${isPod ? "podcast" : "radio"}/${slug}`;
    const shareTitle = currentStation.name;
    const shareText = isPod
      ? (
          t("player.share_message_podcast") ||
          "I'm listening to {name} on StreamEater. Listen here:"
        ).replace("{name}", currentStation.name)
      : (
          t("player.share_message_radio") ||
          "I'm listening to {name} live on StreamEater. Listen here:"
        ).replace("{name}", currentStation.name);

    if (navigator.share) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return;
      } catch (err) {
        if ((err as Error).name !== "AbortError") {
          console.log("Share failed, using clipboard");
        }
      }
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isExpanded) return;
    const currentY = e.touches[0].clientY;
    const startY = touchStartY.current;
    const diff = currentY - startY;

    // Only allow dragging DOWN (positive diff)
    if (diff > 0) {
      setDragOffset(diff);
      // Optional: Dim opacity or scale slightly
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    const diffY = e.changedTouches[0].clientY - touchStartY.current;
    const diffX = e.changedTouches[0].clientX - touchStartX.current;

    // Tap to expand when minimized (small movement = tap)
    if (!isExpanded && Math.abs(diffY) < 20 && Math.abs(diffX) < 20) {
      setIsExpanded(true);
      triggerHaptic(10);
      setDragOffset(0);
      return;
    }

    // Vertical swipe down to minimize (when expanded)
    if (diffY > 100 && isExpanded) {
      setIsExpanded(false);
      triggerHaptic(15);
      if (showHint) {
        setShowHint(false);
        localStorage.setItem("se_has_seen_dock_hint", "true");
      }
    }

    // Horizontal swipe for prev/next (when minimized and for radio)
    if (
      !isExpanded &&
      !isPodcast &&
      Math.abs(diffX) > 60 &&
      Math.abs(diffY) < 50
    ) {
      if (diffX < -60 && onNext) {
        // Swipe left = next
        onNext();
        triggerHaptic(15);
      } else if (diffX > 60 && onPrev) {
        // Swipe right = prev
        onPrev();
        triggerHaptic(15);
      }
    }

    setDragOffset(0); // Reset position
  };

  // Calculate dynamic style for drag
  const cardStyle = {
    transform: isExpanded ? `translateY(${dragOffset}px)` : "none",
    transition:
      dragOffset > 0 ? "none" : "all 0.5s cubic-bezier(0.23,1,0.32,1)",
  };

  const handleMuteToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (volume > 0) {
      prevVolumeRef.current = volume;
      setVolume(0);
    } else {
      setVolume(prevVolumeRef.current || 0.5);
    }
  };

  // Helper for placeholder gradient if image fails
  const getPlaceholderStyle = (name: string) => {
    const colors = [
      "from-indigo-900 to-blue-900",
      "from-purple-900 to-fuchsia-900",
      "from-emerald-900 to-teal-900",
      "from-rose-900 to-red-900",
    ];
    const idx = name.length % colors.length;
    return colors[idx];
  };
  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-30 px-2 md:px-4 pointer-events-none transition-all duration-500 ${isSidebarOpen ? "pr-[420px]" : ""} flex justify-center items-end pb-1 md:pb-6`}
      style={{ paddingBottom: "max(0.25rem, env(safe-area-inset-bottom))" }}
    >
      <div
        className={`max-w-4xl mx-auto pointer-events-auto w-full transition-all duration-500 flex justify-center`}
      >
        <div
          className={`rounded-2xl md:rounded-3xl flex flex-col relative overflow-visible
               ${
                 isExpanded
                   ? "w-full glass-premium touch-pan-y shadow-2xl"
                   : "w-auto cursor-pointer glass-premium hover:scale-105 shadow-lg"
               }`}
          onClick={() => !isExpanded && setIsExpanded(true)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={cardStyle}
        >
          {/* STATION INFO PANEL - Expands upward */}
          {showStationInfo && currentStation && (
            <div className="absolute bottom-full left-0 right-0 mb-2 animate-[slideUp_0.2s_ease-out]">
              <div className="mx-2 md:mx-0 bg-[var(--bg-surface)] dark:bg-[#1a1a1a] rounded-2xl border border-[var(--border-primary)] shadow-2xl overflow-hidden">
                {/* Close button */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowStationInfo(false);
                  }}
                  className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white/70 hover:text-white flex items-center justify-center transition-all"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>

                {/* RESPONSIVE CONTENT: vertical on mobile, horizontal on desktop */}
                <div className="p-4 flex flex-col md:flex-row md:items-center md:gap-6">
                  {/* LEFT SECTION: Logo + Basic Info */}
                  <div className="flex flex-col items-center md:flex-row md:items-center md:gap-4 md:flex-1">
                    {/* Logo */}
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onNavigateToStation) {
                          onNavigateToStation();
                          setShowStationInfo(false);
                        }
                      }}
                      className="w-16 h-16 md:w-20 md:h-20 rounded-xl overflow-hidden shadow-lg border border-white/10 bg-black/50 mb-2 md:mb-0 relative flex-shrink-0 cursor-pointer hover:scale-105 transition-transform group/logo"
                    >
                      {currentStation.logoUrl && !imageError ? (
                        <img
                          src={getStationLogoUrl(currentStation, {
                            width: 300,
                          })}
                          alt={currentStation.name}
                          className="w-full h-full object-cover"
                          onError={() => setImageError(true)}
                        />
                      ) : (
                        <div
                          className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${getPlaceholderStyle(currentStation.name)}`}
                        >
                          <span className="text-white font-black text-lg">
                            {currentStation.name.slice(0, 2).toUpperCase()}
                          </span>
                        </div>
                      )}
                      <div className="absolute top-1 right-1 bg-black/60 backdrop-blur-sm px-1 py-0.5 rounded text-[7px] font-bold uppercase text-white/80">
                        {currentStation.stationType === "mainstream"
                          ? "FM"
                          : "WEB"}
                      </div>
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover/logo:opacity-100 transition-opacity">
                        <span className="text-[10px] font-bold text-white uppercase tracking-wider border border-white/50 px-2 py-1 rounded-full backdrop-blur-sm">
                          View
                        </span>
                      </div>
                    </div>

                    {/* Station Info */}
                    <div className="text-center md:text-left flex-1 min-w-0">
                      <h2 className="text-base md:text-lg font-bold text-[var(--text-primary)] leading-tight truncate">
                        {currentStation.name}
                      </h2>

                      <div className="flex items-center justify-center md:justify-start gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-cyan-400 font-semibold flex items-center gap-1">
                          <Globe size={10} />
                          {tCountry(currentStation.country)}
                        </span>
                        {currentStation.bitrate > 0 && (
                          <span className="text-xs text-[var(--text-secondary)] font-medium px-1.5 py-0.5 bg-[var(--bg-surface-glass)] rounded">
                            {currentStation.bitrate}k
                          </span>
                        )}
                        {currentStation.votes > 0 && (
                          <span className="text-xs text-yellow-400 font-semibold flex items-center gap-1">
                            <Star size={10} fill="currentColor" />
                            {currentStation.votes > 1000
                              ? `${(currentStation.votes / 1000).toFixed(1)}k`
                              : currentStation.votes}
                          </span>
                        )}
                      </div>

                      {/* Tags - styled as pills */}
                      {currentStation.tags &&
                        currentStation.tags.length > 0 && (
                          <div className="flex items-center justify-center md:justify-start gap-1.5 mt-2 flex-wrap">
                            {currentStation.tags.slice(0, 4).map((tag) => (
                              <span
                                key={tag}
                                className="text-[10px] text-[var(--text-secondary)] font-medium px-2 py-0.5 bg-[var(--bg-surface-glass)] rounded-full border border-[var(--border-primary)]"
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                    </div>
                  </div>

                  {/* RIGHT SECTION: Actions */}
                  <div className="flex items-center gap-2 mt-3 md:mt-0 justify-center md:justify-end flex-shrink-0">
                    {/* Share Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShare();
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white/90 hover:text-white border border-white/10 transition-all text-xs font-semibold"
                    >
                      <Share2 size={12} />
                      {t("player.share")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Share Toast */}
          {showShareToast && (
            <div className="fixed bottom-28 left-1/2 -translate-x-1/2 z-50 animate-[slideUp_0.2s_ease-out]">
              <div className="flex items-center gap-2 px-4 py-2 bg-radio-accent text-black rounded-full shadow-lg font-semibold text-sm">
                <Check size={16} />
                {t("player.link_copied")}
              </div>
            </div>
          )}
          {/* HINT OVERLAY */}
          {showHint && isExpanded && (
            <div className="absolute -top-12 left-0 right-0 flex justify-center animate-bounce z-50 pointer-events-none">
              <div className="bg-black/80 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-md border border-white/10 shadow-xl flex items-center gap-2">
                <GripHorizontal size={12} className="opacity-70" />
                <span>Dra ned för att minimera</span>
              </div>
            </div>
          )}
          {/* Podcast Progress Bar (Only when Expanded) */}
          {isPodcast && isExpanded && onSeek && (
            <div className="w-full px-6 pt-6 pb-0 flex flex-col gap-1">
              <div
                className="relative w-full h-1.5 group cursor-pointer"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="absolute inset-0 bg-[var(--bg-surface-glass)] rounded-full"></div>
                <div
                  className="absolute inset-0 bg-radio-accent rounded-full relative z-10"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-[var(--text-invert)] dark:bg-white rounded-full shadow-lg shadow-black/50 scale-100 transition-transform hover:scale-110"></div>
                </div>
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  value={currentTime}
                  onInput={(e) =>
                    onSeek(parseFloat((e.target as HTMLInputElement).value))
                  }
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20"
                />
              </div>
              <div className="flex justify-between text-[10px] font-mono text-[var(--text-secondary)]">
                <span>{formatTime(currentTime)}</span>
                <span className={onSeek ? "" : "opacity-0"}>
                  {formatTime(duration)}
                </span>
              </div>
            </div>
          )}

          {/* Drag Handle (Visual Only) */}
          {isExpanded && (
            <div className="w-full flex justify-center py-2 absolute top-0 left-0 touch-none">
              <div className="w-8 h-1 bg-white/20 rounded-full" />
            </div>
          )}

          <div
            className={`flex items-center w-full ${isExpanded ? "p-4 gap-4" : "px-3 py-2 gap-3"}`}
          >
            {isExpanded && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(false);
                }}
                className="absolute -top-5 left-1/2 -translate-x-1/2 px-4 h-9 flex items-center gap-1.5 justify-center rounded-full text-[11px] font-semibold text-[var(--text-primary)] transition-all z-10 backdrop-blur-xl border border-[var(--glass-border)] shadow-[0_10px_30px_rgba(0,0,0,0.2)] bg-[var(--glass-bg)] hover:shadow-[0_12px_36px_rgba(0,0,0,0.28)] hover:-translate-y-0.5"
                aria-label="Collapse Player"
              >
                <ChevronDown size={14} className="opacity-80" />
                <span className="uppercase tracking-[0.12em] text-[10px] hidden md:inline">
                  Dock
                </span>
              </button>
            )}

            {showEq && isExpanded && (
              <Equalizer
                gains={eqGains}
                onGainChange={onEqGainChange}
                onClose={() => setShowEq(false)}
              />
            )}

            {/* Cover Art Area */}
            <div
              onClick={(e) => {
                // Only handle station info toggle when expanded
                // When minimized, let the click propagate to parent to expand
                if (isExpanded && currentStation) {
                  e.stopPropagation();
                  setShowStationInfo(!showStationInfo);
                }
              }}
              className={`relative z-20 flex-shrink-0 flex items-center justify-center cursor-pointer group transition-all duration-500
                        ${
                          isExpanded
                            ? "w-14 h-14 md:w-20 md:h-20"
                            : "w-10 h-10 hover:scale-110"
                        }`}
              role="button"
              aria-label="Show station info"
            >

              {isPodcast ? (
                // PODCAST STYLE: Rounded Rectangle (No spin, square-ish)
                <div
                  className={`relative w-full h-full rounded-xl shadow-xl overflow-hidden bg-[var(--bg-card)] dark:bg-gray-900 border border-[var(--border-primary)] group-hover:border-radio-accent/50 transition-colors`}
                >
                  {currentStation?.logoUrl && !imageError ? (
                    <img
                      src={getStationLogoUrl(currentStation, { width: 300 })}
                      className="w-full h-full object-cover"
                      alt={currentStation.name}
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div
                      className={`w-full h-full bg-gradient-to-br ${currentStation ? getPlaceholderStyle(currentStation.name) : "from-gray-800 to-black"} flex items-center justify-center`}
                    >
                      {currentStation ? (
                        <span className="text-white font-black text-lg">
                          {currentStation.name.slice(0, 2).toUpperCase()}
                        </span>
                      ) : (
                        <Podcast size={20} className="text-white/40" />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                // RADIO STYLE: Vinyl (Circular, Spinning)
                <div
                  className={`relative w-full h-full rounded-full shadow-xl bg-[var(--bg-card)] dark:bg-[#111] overflow-hidden ${isPlaying ? "animate-[spin_6s_linear_infinite]" : ""}`}
                  style={{
                    animationPlayState: isPlaying ? "running" : "paused",
                  }}
                >
                  {/* 1. BASE LAYER: Default SE Vinyl */}
                  <div className="absolute inset-0">
                    <div className="absolute inset-0 rounded-full bg-[repeating-radial-gradient(currentColor_0,currentColor_2px,transparent_3px)] opacity-20 text-gray-700"></div>
                    <div className="absolute inset-0 rounded-full bg-[conic-gradient(transparent_0deg,rgba(255,255,255,0.1)_40deg,transparent_80deg)]"></div>
                    <div className="absolute inset-0 flex items-center justify-center z-10">
                      <div className="w-1/2 h-1/2 rounded-full bg-gray-200 dark:bg-[#222] flex items-center justify-center shadow-inner">
                        <span
                          className={`font-black text-black dark:text-gray-300 tracking-tighter leading-none font-display select-none ${isExpanded ? "text-[24px] md:text-[36px]" : "text-[16px]"}`}
                        >
                          SE
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* 2. IMAGE LAYER: Station Logo */}
                  {currentStation?.logoUrl && !imageError ? (
                    <img
                      src={getStationLogoUrl(currentStation, { width: 300 })}
                      className={`absolute inset-0 w-full h-full object-contain z-20 rounded-full p-[8%]`}
                      alt={currentStation.name}
                      onError={() => setImageError(true)}
                    />
                  ) : currentStation ? (
                    <div
                      className={`absolute inset-0 z-20 flex items-center justify-center bg-gradient-to-br ${getPlaceholderStyle(currentStation.name)} opacity-80 mix-blend-overlay`}
                    >
                      <span className="text-white/80 font-black text-xl">
                        {currentStation.name.slice(0, 2).toUpperCase()}
                      </span>
                    </div>
                  ) : null}

                  {/* Play Overlay */}
                  <div
                    className={`absolute inset-0 bg-[color:var(--overlay-soft)] flex items-center justify-center transition-opacity ${currentStation?.id && isPlaying ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
                  >
                    {currentStation?.id && isPlaying ? (
                      <div className="flex gap-0.5 h-3 items-end">
                        <div className="w-0.5 bg-white animate-[soundWave_1s_ease-in-out_infinite]"></div>
                        <div className="w-0.5 bg-white animate-[soundWave_1.2s_ease-in-out_infinite_0.1s]"></div>
                        <div className="w-0.5 bg-white animate-[soundWave_0.8s_ease-in-out_infinite_0.2s]"></div>
                      </div>
                    ) : (
                      <Play className="text-white" size={12} />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Meta Info */}
            <div
              onClick={(e) => {
                if (isExpanded && currentStation && onNavigateToStation) {
                  e.stopPropagation();
                  onNavigateToStation();
                }
                // If not expanded, let it bubble to parent to handle expansion
              }}
              className={`flex-1 min-w-0 flex items-center transition-all duration-500 cursor-pointer group/info relative z-20 active:scale-95 px-2`}
            >
              {/* Inline Favorite Button (Left) - REMOVED */}

              <div className="flex flex-col flex-1 min-w-0 justify-center">
                  <h4
                    className={`font-bold text-[var(--text-primary)] group-hover/info:text-radio-accent leading-tight transition-all duration-300 ${isExpanded ? "text-sm md:text-lg mb-0.5 tracking-tight line-clamp-2 md:line-clamp-1" : "text-xs md:text-sm mb-0 truncate"}`}
                  >
                    {currentStation
                      ? isPodcast
                        ? currentStation?.lastEpisodeTitle || currentStation?.name
                        : currentStation.name
                      : "READY PLAY"}
                  </h4>
                  <div
                    className={`flex items-center text-xs text-radio-secondary truncate gap-2 transition-all duration-300 ${!isExpanded ? "h-0 opacity-0 overflow-hidden" : "h-auto opacity-100"}`}
                  >
                    {currentStation ? (
                      <>
                        <span className="text-[var(--text-secondary)] font-mono uppercase text-[9px] tracking-wider">
                          {isPodcast 
                            ? currentStation.name 
                            : (currentStation.country === "The United States" || currentStation.country === "United States") 
                              ? "USA" 
                              : currentStation.country}
                        </span>
                        {currentStation.bitrate > 0 && (
                          <span className="bg-white/5 px-1 rounded text-[9px] font-mono">
                            {currentStation.bitrate}k
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="font-mono text-[9px] tracking-widest opacity-50 text-[var(--text-secondary)]">
                        SELECT STATION
                      </span>
                    )}
                  </div>
              </div>
              
              {/* Context Indicator - Absolute mostly works but let's keep it safe given the layout change */}
              {isExpanded && contextInfo && contextInfo.totalItems > 1 && (
                <div className="absolute bottom-[-16px] left-0 right-0 flex items-center justify-center gap-1.5 text-[10px] text-[var(--text-secondary)] opacity-70">
                  <Layers size={10} className="opacity-60" />
                  <span className="font-mono">
                    {contextInfo.currentIndex}/{contextInfo.totalItems}
                  </span>
                </div>
              )}
            </div>

            {/* Visualizer (Only for Radio) */}
            {isExpanded && !isPodcast && currentStation && (
              <div className="hidden md:flex flex-[2] h-12 px-6 items-center justify-center opacity-80 overflow-hidden">
                <HeaderOscilloscope
                  isPlaying={isPlaying}
                  stationId={currentStation?.id}
                />
              </div>
            )}

            {/* Controls */}
            <div className="flex items-center z-30 gap-2 md:gap-4">
              {/* Shuffle Button */}
              {isExpanded && onShuffle && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShuffle();
                  }}
                  className={`hidden md:flex items-center justify-center p-2 rounded-full transition-all ${
                    isShuffling
                      ? "text-radio-accent bg-radio-accent/10"
                      : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--text-primary)]/10"
                  }`}
                  title="Shuffle"
                >
                  <Shuffle size={16} />
                </button>
              )}

              {/* Skip Buttons (Podcasts Only) */}
              {isExpanded && isPodcast && onSkip && (
                <div className="hidden md:flex items-center gap-4">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSkip(-15);
                    }}
                    className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:scale-110 transition-all"
                    title="-15s"
                    aria-label="Rewind 15 seconds"
                  >
                    <RotateCcw size={16} />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSkip(30);
                    }}
                    className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:scale-110 transition-all"
                    title="+30s"
                    aria-label="Skip forward 30 seconds"
                  >
                    <RotateCw size={16} />
                  </button>
                  {/* Playback Speed Toggle */}
                  {onCyclePlaybackSpeed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCyclePlaybackSpeed();
                      }}
                      className="text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:scale-110 transition-all text-xs font-bold min-w-[32px] px-1.5 py-0.5 rounded bg-white/5 hover:bg-white/10"
                      title={`Playback speed: ${playbackSpeed}x (click to change)`}
                      aria-label={`Current speed ${playbackSpeed}x, click to change`}
                    >
                      {playbackSpeed}x
                    </button>
                  )}
                </div>
              )}

              <div
                className={`flex items-center gap-2 md:gap-4 transition-all duration-500 ${!isExpanded ? "opacity-0 w-0 overflow-hidden" : "opacity-100"}`}
              >
                  {/* Favorite button moved to cover art area (or removed from here according to plan) */}
              </div>

              {/* Unified Prev/Play/Next block */}
              <div
                className={`flex items-center transition-all duration-500 flex-shrink-0 gap-2 md:gap-4`}
              >
                {/* Prev button or Skip Back */}
                {!isPodcast && onPrev ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onPrev();
                    }}
                    className={`flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all ${isExpanded ? "w-10 h-10 hover:scale-110 md:w-12 md:h-12" : "w-8 h-8 opacity-80"}`}
                    title="Previous Station"
                    aria-label="Previous Station"
                  >
                    <SkipBack size={isExpanded ? 24 : 16} fill="currentColor" />
                  </button>
                ) : isPodcast && onSkip ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSkip(-15);
                    }}
                    className={`flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all ${isExpanded ? "w-10 h-10 hover:scale-110 md:w-12 md:h-12" : "w-8 h-8 opacity-80"}`}
                    title="Rewind 15s"
                    aria-label="Rewind 15 seconds"
                  >
                    <div className="relative flex items-center justify-center">
                      <RotateCcw
                        size={isExpanded ? 24 : 18}
                        strokeWidth={2.5}
                      />
                      <span
                        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-bold leading-none ${isExpanded ? "text-[8px] mt-[1px]" : "text-[6px] mt-[1px]"}`}
                      >
                        15
                      </span>
                    </div>
                  </button>
                ) : null}
                <button
                  className={`rounded-full flex items-center justify-center transition-all z-10 group shadow-lg
                                    ${
                                      isExpanded
                                        ? "w-12 h-12 md:w-16 md:h-16 bg-[var(--text-primary)] text-[var(--bg-main)] hover:scale-105 hover:bg-[var(--text-primary)]/90"
                                        : "w-10 h-10 bg-[var(--text-primary)] text-[var(--bg-main)] hover:scale-105"
                                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    triggerHaptic(10);
                    onTogglePlay();
                  }}
                  aria-label={isPlaying ? "Pause Station" : "Play Station"}
                >
                  {isPlaying ? (
                    <Pause size={isExpanded ? 24 : 16} fill="currentColor" />
                  ) : (
                    <Play
                      size={isExpanded ? 24 : 16}
                      className="ml-1"
                      fill="currentColor"
                    />
                  )}
                </button>
                {/* Next button or Skip Forward */}
                {!isPodcast && onNext ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onNext();
                    }}
                    className={`flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all ${isExpanded ? "w-10 h-10 hover:scale-110 md:w-12 md:h-12" : "w-8 h-8 opacity-80"}`}
                    title="Next Station"
                    aria-label="Next Station"
                  >
                    <SkipForward
                      size={isExpanded ? 24 : 16}
                      fill="currentColor"
                    />
                  </button>
                ) : isPodcast && onSkip ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onSkip(30);
                    }}
                    className={`flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-all ${isExpanded ? "w-10 h-10 hover:scale-110 md:w-12 md:h-12" : "w-8 h-8 opacity-80"}`}
                    title="Fast Forward 30s"
                    aria-label="Fast Forward 30 seconds"
                  >
                    <div className="relative flex items-center justify-center">
                      <RotateCw size={isExpanded ? 24 : 18} strokeWidth={2.5} />
                      <span
                        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 font-bold leading-none ${isExpanded ? "text-[8px] mt-[1px]" : "text-[6px] mt-[1px]"}`}
                      >
                        30
                      </span>
                    </div>
                  </button>
                ) : null}
              </div>

              <div
                className={`flex items-center gap-2 md:gap-4 transition-all duration-500 ${!isExpanded ? "opacity-0 w-0 overflow-hidden" : "opacity-100"}`}
              >
                <div className="hidden md:flex items-center gap-2 md:gap-3 group bg-[color:var(--bg-surface-glass)] p-2 rounded-full">
                  <button
                    onClick={handleMuteToggle}
                    className="w-5 flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
                    aria-label={volume === 0 ? "Unmute" : "Mute"}
                  >
                    {volume === 0 ? (
                      <VolumeX size={12} />
                    ) : (
                      <Volume2 size={12} />
                    )}
                  </button>
                  <div className="relative w-16 md:w-20 h-1 flex items-center cursor-pointer">
                    <div className="absolute inset-0 bg-[var(--bg-surface)] rounded-full overflow-hidden pointer-events-none">
                      <div
                        className="h-full bg-[var(--text-primary)] transition-all duration-75"
                        style={{ width: `${volume * 100}%` }}
                      ></div>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={volume}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => setVolume(parseFloat(e.target.value))}
                      className="w-full h-full opacity-0 cursor-pointer absolute inset-0 z-10"
                      aria-label="Volume"
                    />
                  </div>
                </div>

                {!isPodcast && currentStation && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleFavorite(currentStation);
                    }}
                    className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all group
                                      ${
                                        favorites.some((f) => f.id === currentStation.id)
                                          ? "text-radio-accent scale-105"
                                          : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-white/5"
                                      }`}
                    title={favorites.some((f) => f.id === currentStation.id) ? "Remove from Favorites" : "Add to Favorites"}
                    aria-label="Toggle Favorite"
                  >
                    <Star
                      size={isExpanded ? 20 : 16}
                      fill={favorites.some((f) => f.id === currentStation.id) ? "currentColor" : "none"}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayerBar;
